/**
 * VerifyEmailScreen - redeem the 6-digit signup code.
 *
 * Reached from RegisterScreen after a 201, and from LoginScreen on a 403
 * email_not_verified; both prefill the address. On success it stores the token
 * and flips isAuthenticated, which makes AuthNavigator swap to the authenticated
 * stack — there is no explicit navigation on success.
 */

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Button } from '../../components/ui/Button';
import { CodeInput } from '../../components/ui/CodeInput';
import { verifyEmail, resendVerification } from '../../services/authApi';
import { storeToken } from '../../services/tokenStorage';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage';
import { RequestError } from '../../utils/apiError';
import { showError, showSuccess, showInfo } from '../../utils/toast';

export function VerifyEmailScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const email = (route.params as { email?: string } | undefined)?.email ?? '';

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [fieldError, setFieldError] = useState<string | undefined>();

  const { setIsAuthenticated } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const getApiErrorMessage = useApiErrorMessage();
  const { secondsLeft, start, isCoolingDown } = useResendCooldown();

  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const handleVerify = async () => {
    setVerifying(true);
    setFieldError(undefined);
    try {
      const response = await verifyEmail({ email, code });
      await storeToken(response.access_token);
      setIsAuthenticated(true);
      showSuccess(t('auth.emailVerified'));
    } catch (error) {
      setFieldError(getApiErrorMessage(error, 'common.error'));
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const response = await resendVerification(email);
      start(response.resend_available_in_seconds);
      showInfo(t('auth.codeSent'));
    } catch (error) {
      if (error instanceof RequestError && error.retryAfterSeconds !== undefined) {
        start(error.retryAfterSeconds);
      }
      showError(getApiErrorMessage(error, 'auth.errResendTooSoon'));
    } finally {
      setResending(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('auth.verifyEmailTitle')}</Text>
      <Text style={styles.subtitle}>{t('auth.verifyEmailSubtitle', { email })}</Text>

      <View style={styles.form}>
        <CodeInput
          value={code}
          onChangeText={setCode}
          label={t('auth.codeLabel')}
          error={fieldError}
          autoFocus
          testID="code-input"
        />

        <Button
          title={t('auth.verifyButton')}
          onPress={handleVerify}
          loading={verifying}
          disabled={code.length !== 6}
          style={styles.verifyButton}
        />

        <Button
          title={
            isCoolingDown ? t('auth.resendIn', { seconds: secondsLeft }) : t('auth.resendCode')
          }
          onPress={handleResend}
          variant="secondary"
          loading={resending}
          disabled={isCoolingDown || resending}
          style={styles.resendButton}
        />

        <TouchableOpacity onPress={() => navigation.navigate('Login' as never)}>
          <Text style={styles.backLink}>{t('auth.backToLogin')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      padding: 20,
      backgroundColor: theme.colors.background,
    },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      marginBottom: 8,
      textAlign: 'center',
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      marginBottom: 32,
      textAlign: 'center',
    },
    form: {
      width: '100%',
    },
    verifyButton: {
      marginTop: 8,
    },
    resendButton: {
      marginTop: 12,
    },
    backLink: {
      marginTop: 24,
      textAlign: 'center',
      color: theme.colors.primary,
      fontWeight: '600',
    },
  });
}
